/**
 * Resolve a task artifact to something the RM can actually open (TASK-702).
 *
 * `task_artifacts` stores POINTERS — a type and an id in some other store. Turning one into a
 * destination is the only thing standing between "the agent produced something" and the RM
 * being able to read it before signing off.
 *
 * Two rules, both learned from the one live artifact on the demo account:
 *
 *  1. **An unknown type says so.** Never a dead link, never a blank pane. The reviewer is about
 *     to make a decision; "we can't open this" is information, a broken page is not.
 *  2. **Resolution can fail for a KNOWN type too.** The live `email_draft` points at
 *     `client_id 17f37852-8772d2ba-…`, which resolves to no client — the de-identifier
 *     corrupted the UUID (TASK-693). That is not papered over here.
 *
 * Deliberately narrow: only the types that already have a real opening path. Building
 * resolvers for artifact types nothing produces yet — including TASK-703's verdict type, whose
 * store does not exist — would be inventing destinations.
 */
import type { TaskArtifact } from '../../types/tasks';

export type ArtifactTarget =
  /** A route the RM can be sent to. */
  | { kind: 'route'; to: string; label: string }
  /** A known type we cannot open right now, with the reason stated plainly. */
  | { kind: 'unresolvable'; label: string; reason: string }
  /** A type this build has no opening path for at all. */
  | { kind: 'unsupported'; label: string; reason: string };

/** Human labels — the reviewer reads the TYPE, so it must not lie about what it is. */
const LABELS: Record<string, string> = {
  email_draft: 'Email draft',
  document: 'Document',
  brief: 'Meeting brief',
  dossier: 'Research dossier',
  proposal: 'Portfolio proposal',
  profile_summary: 'Client profile summary',
  // TASK-703. Deliberately not "compliance review": one type carries both the compliance and
  // the suitability verdict, and the record itself says which. A label that guessed would be
  // wrong half the time on the one surface whose job is describing what the agent produced.
  regulated_verdict: 'Regulated verdict',
};

export function artifactLabel(artifact: Pick<TaskArtifact, 'artifact_type'>): string {
  return LABELS[artifact.artifact_type] ?? artifact.artifact_type;
}

export function resolveArtifact(artifact: TaskArtifact): ArtifactTarget {
  const label = artifactLabel(artifact);

  // The server probed the artifact's store. It knows whether the record exists; the client
  // never can. When it has spoken, its answer wins — including when it says a type this file
  // knows how to route cannot be opened.
  const resolved = artifact.resolution;
  if (resolved) {
    if (resolved.state === 'available' && resolved.route) {
      return { kind: 'route', to: resolved.route, label };
    }
    if (resolved.state === 'unsupported') {
      return {
        kind: 'unsupported',
        label,
        reason: resolved.reason ?? `This build has no way to open a ${label.toLowerCase()}.`,
      };
    }
    return {
      kind: 'unresolvable',
      label,
      reason:
        resolved.reason ??
        'This artifact cannot be opened because its source record is missing.',
    };
  }

  // Fallback for a server that does not resolve yet. It can only offer a route by type, which
  // is exactly the weakness the resolution field exists to remove — so it stays a fallback.
  const id = (artifact.artifact_id || '').trim();

  if (!id) {
    return {
      kind: 'unresolvable',
      label,
      reason: 'The agent recorded this artifact without an id, so there is nothing to open.',
    };
  }

  switch (artifact.artifact_type) {
    case 'email_draft':
      return { kind: 'route', to: `/drafts/${id}`, label };
    case 'document':
    case 'brief':
      // Both are workbench-backed documents and share its opening path.
      return { kind: 'route', to: `/workbench/${id}`, label };
    default:
      return {
        kind: 'unsupported',
        label,
        reason: `This build has no way to open a ${label.toLowerCase()}.`,
      };
  }
}

/**
 * Whether sign-off must wait until this artifact has been opened.
 *
 * Only artifacts that RESOLVE gate sign-off. Making it unconditional would leave the one live
 * `email_draft` — whose client id was corrupted by the de-identifier — permanently unsignable:
 * the reviewer would be blocked forever by an id they cannot fix. Block on nothing; say so
 * instead.
 */
export function gatesSignOff(artifact: TaskArtifact): boolean {
  return resolveArtifact(artifact).kind === 'route';
}

/**
 * Whether sign-off must wait for an explicit acknowledgement that the work could not be read.
 *
 * The escape hatch above is necessary — a permanently unsignable task helps nobody — but it
 * must not be free. An artifact the RM cannot open is still an artifact they have not read,
 * and clicking ✓ on it is a decision, not a default. So the button stays disabled until the
 * RM says, in one click, that they are signing off on work they could not see.
 */
export function requiresAcknowledgement(artifact: TaskArtifact): boolean {
  return resolveArtifact(artifact).kind !== 'route';
}
